'use strict';

var jsxRuntime = require('@emotion/react/jsx-runtime');
var React = require('react');
var Filter_web = require('./Filter/Filter.web.cjs');
var Item_web = require('./Item/Item.web.cjs');
var ChipContext = require('./context/ChipContext.cjs');
var Chip_web_styles = require('./Chip.web.styles.cjs');

const ChipComponent = /*#__PURE__*/ React.forwardRef(({ variant = "single", value, onValueChange, children, customCss, defaultValue, disabled = false, ...props }, ref)=>{
    const [internalValue, setInternalValue] = React.useState(()=>{
        if (defaultValue !== undefined) {
            return defaultValue;
        }
        return variant === "multi" ? [] : "";
    });
    // Use controlled value if provided, otherwise use internal state
    const currentValue = value !== undefined ? value : internalValue;
    const handleSelectionChange = React.useCallback((itemValue)=>{
        let newValue;
        if (variant === "single") {
            newValue = currentValue === itemValue ? "" : itemValue;
        } else {
            const currentArray = Array.isArray(currentValue) ? currentValue : [];
            newValue = currentArray.includes(itemValue) ? currentArray.filter((v)=>v !== itemValue) : [
                ...currentArray,
                itemValue
            ];
        }
        if (value === undefined) {
            setInternalValue(newValue);
        }
        onValueChange?.(newValue);
    }, [
        currentValue,
        onValueChange,
        value,
        variant
    ]);
    const contextValue = React.useMemo(()=>({
            disabled,
            variant,
            selectedValues: currentValue,
            onSelectionChange: handleSelectionChange
        }), [
        variant,
        currentValue,
        disabled,
        handleSelectionChange
    ]);
    return /*#__PURE__*/ jsxRuntime.jsx(ChipContext.ChipContext.Provider, {
        value: contextValue,
        children: /*#__PURE__*/ jsxRuntime.jsx("div", {
            ref: ref,
            css: [
                Chip_web_styles.chipBase,
                customCss
            ],
            role: variant === "single" ? "radiogroup" : "group",
            "data-testid": "aviary-chip",
            ...props,
            children: children
        })
    });
});
/**
 * A compound component for managing a group of interactive chips (tags, filters, or selectable options).
 * Supports both single and multi variant selection modes through the variant prop.
 *
 * Usage:
 * ```tsx
 * <Chip variant="multi" onValueChange={handleChange}>
 *   <Chip.Item value="option1">Option 1</Chip.Item>
 *   <Chip.Item value="option2">Option 2</Chip.Item>
 * </Chip>
 * ```
 *
 * {@link https://aviary.fullscript.io/latest/components/chip/web-u0hBivE1 | Documentation}
 *
 * {@link https://git.fullscript.io/developers/aviary/-/blob/development/packages/aviary-web/src/components/molecules/Chip/Chip.web.tsx | View in GitLab}
 *
 * @scope Core
 * @compound Chip.Item - Individual selectable chip option
 * @compound Chip.Filter - Filterable chip variant
 *
 */ const Chip = Object.assign(ChipComponent, {
    Item: Item_web.Item,
    Filter: Filter_web.Filter
});

exports.Chip = Chip;
//# sourceMappingURL=Chip.web.cjs.map
